"use client"

import { useEffect, useRef, useCallback } from "react"

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  radius: number
}

const CONNECTION_DISTANCE = 150
const MOUSE_RADIUS = 200
const MOUSE_FORCE = 0.02

function getParticleCount() {
  if (typeof window === "undefined") return 40
  if (window.innerWidth < 768) return 25
  return 70
}

export function ParticleNetwork({ className = "" }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const particlesRef = useRef<Particle[]>([])
  const mouseRef = useRef({ x: -9999, y: -9999 })
  const animationRef = useRef<number>(0)
  const sizeRef = useRef({ w: 0, h: 0 })
  const cachedColorRef = useRef<string>("hsl(220, 70%, 50%)")

  const initParticles = useCallback((width: number, height: number) => {
    const count = getParticleCount()
    const particles: Particle[] = []
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 1.5 + 1,
      })
    }
    particlesRef.current = particles
  }, [])

  useEffect(() => {
    // Respect prefers-reduced-motion
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
    if (motionQuery.matches) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const updateColor = () => {
      const style = getComputedStyle(canvas)
      const primary = style.getPropertyValue("--primary").trim()
      if (primary) {
        cachedColorRef.current = `hsl(${primary})`
      }
    }

    const updateSize = () => {
      const parent = canvas.parentElement
      if (!parent) return
      const { width, height } = parent.getBoundingClientRect()
      const dpr = window.devicePixelRatio || 1
      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx.scale(dpr, dpr)
      sizeRef.current = { w: width, h: height }

      if (particlesRef.current.length === 0) {
        initParticles(width, height)
      }
    }

    updateSize()
    updateColor()

    // Update color on theme change
    const observer = new MutationObserver(updateColor)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] })

    const resizeObserver = new ResizeObserver(updateSize)
    resizeObserver.observe(canvas.parentElement!)

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    }

    const handleMouseLeave = () => {
      mouseRef.current = { x: -9999, y: -9999 }
    }

    // Only add mouse listeners on non-touch devices
    const isTouch = window.matchMedia("(pointer: coarse)").matches
    if (!isTouch) {
      canvas.addEventListener("mousemove", handleMouseMove)
      canvas.addEventListener("mouseleave", handleMouseLeave)
    }

    const animate = () => {
      const { w, h } = sizeRef.current
      if (w === 0 || h === 0) {
        animationRef.current = requestAnimationFrame(animate)
        return
      }

      const dpr = window.devicePixelRatio || 1
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)

      const color = cachedColorRef.current
      const particles = particlesRef.current
      const mouse = mouseRef.current

      // Update particles
      for (const p of particles) {
        // Mouse interaction
        const dx = mouse.x - p.x
        const dy = mouse.y - p.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist < MOUSE_RADIUS && dist > 0) {
          const force = (MOUSE_RADIUS - dist) / MOUSE_RADIUS * MOUSE_FORCE
          p.vx -= (dx / dist) * force
          p.vy -= (dy / dist) * force
        }

        p.x += p.vx
        p.y += p.vy

        // Bounce off edges
        if (p.x < 0 || p.x > w) p.vx *= -1
        if (p.y < 0 || p.y > h) p.vy *= -1
        p.x = Math.max(0, Math.min(w, p.x))
        p.y = Math.max(0, Math.min(h, p.y))

        // Dampen velocity
        p.vx *= 0.999
        p.vy *= 0.999

        // Maintain minimum speed
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy)
        if (speed < 0.15) {
          p.vx += (Math.random() - 0.5) * 0.1
          p.vy += (Math.random() - 0.5) * 0.1
        }
      }

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x
          const dy = particles[i].y - particles[j].y
          const dist = Math.sqrt(dx * dx + dy * dy)

          if (dist < CONNECTION_DISTANCE) {
            const opacity = (1 - dist / CONNECTION_DISTANCE) * 0.2
            ctx.beginPath()
            ctx.strokeStyle = color.replace(")", ` / ${opacity})`)
            ctx.lineWidth = 0.5
            ctx.moveTo(particles[i].x, particles[i].y)
            ctx.lineTo(particles[j].x, particles[j].y)
            ctx.stroke()
          }
        }
      }

      // Draw particles
      for (const p of particles) {
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2)
        ctx.fillStyle = color.replace(")", " / 0.4)")
        ctx.fill()
      }

      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(animationRef.current)
      resizeObserver.disconnect()
      observer.disconnect()
      if (!isTouch) {
        canvas.removeEventListener("mousemove", handleMouseMove)
        canvas.removeEventListener("mouseleave", handleMouseLeave)
      }
    }
  }, [initParticles])

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-none ${className}`}
      aria-hidden="true"
    />
  )
}
